import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';

import { LoadingPage } from '@nesso/ui/blocks/loading-page';

import type { SelectedClient } from '@/entities/client';
import { clientByIdOptions } from '@/entities/client';
import {
    isFirstDictationPending,
    PermissionGate,
    preferencesOptions,
} from '@/entities/practice-member';
import { sessionsByDayOptions } from '@/entities/session';
import {
    createPortalSessionOptions,
    PaymentFailedAlert,
    SubscriptionStatusAlert,
    subscriptionMeOptions,
    useReactivateSubscriptionOptions,
} from '@/entities/subscription';
import { SearchClientField } from '@/features/client/search-client';

import { DictateRecap } from './new-session/dictate-recap';
import { FirstDictationBanner } from './new-session/first-dictation-banner';

type Props = {
    hasEhrAccess: boolean;
};

export function NewSession({ hasEhrAccess }: Props) {
    // undefined = untouched (sample client applies); null = cleared, and must stay cleared
    const [clientChoice, setClientChoice] = useState<SelectedClient | null | undefined>(undefined);
    const [hasError, setHasError] = useState(false);

    const { data: subscription } = useQuery(subscriptionMeOptions());
    const { data: todaySessions = [], isPending: isLoadingTodaySessions } = useQuery(
        sessionsByDayOptions(),
    );
    const { data: preferences, isPending: isLoadingPreferences } = useQuery(preferencesOptions());

    const firstDictationPending = isFirstDictationPending(preferences);
    const sampleClientId = firstDictationPending
        ? preferences?.['welcome-guide']?.sampleClientId
        : undefined;
    const { data: sampleClient, isLoading: isLoadingSampleClient } = useQuery({
        ...clientByIdOptions(sampleClientId ?? ''),
        enabled: !!sampleClientId,
    });
    const selectedClient = clientChoice !== undefined ? clientChoice : (sampleClient ?? null);
    // Cues render as soon as preferences resolve, a round trip before the sample client does.
    // Starting in that window would take the missing-client branch instead of pre-filling.
    const isAwaitingSampleClient = clientChoice === undefined && isLoadingSampleClient;

    const navigate = useNavigate();
    const portalSession = useMutation(createPortalSessionOptions());
    const handleBillingDetails = () => portalSession.mutate(undefined);
    const reactivate = useMutation(useReactivateSubscriptionOptions());
    const handleReactivate = () => reactivate.mutate();

    const handleNavigateToRecording = (clientId: string) => {
        navigate({ to: '/recording', search: { clientId } });
    };

    const handleStartDictation = () => {
        if (!selectedClient) {
            setHasError(true);
            return;
        }
        handleNavigateToRecording(selectedClient.id);
    };

    const handleSelectClient = (client: SelectedClient) => {
        setClientChoice(client);
        setHasError(false);
    };

    const handleClearClient = () => {
        setClientChoice(null);
        setHasError(false);
    };

    if (portalSession.isPending || portalSession.isSuccess) {
        return <LoadingPage className="fixed inset-0 z-50" />;
    }

    const clientSearch = (
        <SearchClientField
            variant="inline"
            layout="dropdown"
            hasEhrAccess={hasEhrAccess}
            startSessionOnCreate
            selectedClient={selectedClient}
            hasError={hasError}
            onSelectClient={handleSelectClient}
            onClear={handleClearClient}
        />
    );

    const sampleSessionId = preferences?.['welcome-guide']?.sampleSessionId;
    const hasRecordedToday =
        !isLoadingTodaySessions &&
        !isLoadingPreferences &&
        todaySessions.some((session) => session.id !== sampleSessionId);

    return (
        <div className="flex flex-col gap-6 sm:gap-4 sm:mt-[5.65625rem] w-full xs:w-auto">
            {subscription &&
                match([subscription.status, subscription.cancelAtPeriodEnd] as const)
                    .with(['CANCELED', P._], () => (
                        <SubscriptionStatusAlert
                            variant="expired"
                            responsive="viewport"
                            retentionEndsAt={subscription.retentionEndsAt}
                            onAction={() => navigate({ to: '/settings/plan-billing' })}
                        />
                    ))
                    .with(['PAST_DUE', P._], () => (
                        <PaymentFailedAlert
                            responsive="viewport"
                            retentionEndsAt={subscription.retentionEndsAt}
                            onAction={handleBillingDetails}
                        />
                    ))
                    .with([P._, true], () => (
                        <SubscriptionStatusAlert
                            variant="cancelling"
                            responsive="viewport"
                            endsAt={subscription.currentPeriodEnd}
                            onAction={handleReactivate}
                            isReactivating={reactivate.isPending || reactivate.isSuccess}
                        />
                    ))
                    .otherwise(() => null)}
            <PermissionGate
                requiredAccessSources={['CLINICIAN', 'NOTES_BASE', 'NOTES_ACTIVE']}
                fallback={(missing) => (
                    <DictateRecap
                        hasRecordedToday={hasRecordedToday}
                        disabled
                        disabledReason={match(missing)
                            .when(
                                (m) => m.includes('NOTES_BASE'),
                                () => 'Add Notes to your plan to start dictating',
                            )
                            .when(
                                (m) => m.includes('NOTES_ACTIVE'),
                                () => 'Update billing information to start dictating',
                            )
                            .otherwise(() => undefined)}
                    >
                        {clientSearch}
                    </DictateRecap>
                )}
            >
                {firstDictationPending && <FirstDictationBanner />}
                <DictateRecap
                    hasRecordedToday={hasRecordedToday}
                    onStartDictation={handleStartDictation}
                    startDisabled={isAwaitingSampleClient}
                    showBeacon={firstDictationPending}
                >
                    {clientSearch}
                </DictateRecap>
            </PermissionGate>
        </div>
    );
}
